import { LIMITS } from './constants';
import type { RegistrantType } from './models';
import { parseIsraeliPhone, PHONE_ERROR_MESSAGES } from './phone';

/**
 * כללי ההרשמה לעבודה — משותפים לטופס (web) ולשרת (worker), כך שהבדיקה זהה בשני הצדדים.
 * קבלן מזין כמות עובדים; עובד עצמאי נספר תמיד כעובד אחד.
 */

export const REGISTRANT_TYPE_LABELS: Record<RegistrantType, string> = {
  contractor: 'קבלן',
  worker: 'עובד',
};

/** כמות העובדים בפועל לפי סוג הנרשם. */
export function effectiveWorkerCount(type: RegistrantType, requested: number): number {
  return type === 'worker' ? 1 : requested;
}

export interface RegistrationInput {
  registrantType: RegistrantType;
  fullName: string;
  phone: string;
  /** נדרש רק לקבלן */
  workerCount?: number;
}

export interface ValidRegistration {
  registrantType: RegistrantType;
  fullName: string;
  phoneE164: string;
  workerCount: number;
}

export type RegistrationValidation =
  | { ok: true; value: ValidRegistration }
  | { ok: false; errors: Partial<Record<'registrantType' | 'fullName' | 'phone' | 'workerCount', string>> };

/** maxWorkers: מהגדרות המערכת (ברירת מחדל LIMITS.maxWorkers) */
export function validateRegistration(input: RegistrationInput, maxWorkers: number = LIMITS.maxWorkers): RegistrationValidation {
  const errors: Partial<Record<'registrantType' | 'fullName' | 'phone' | 'workerCount', string>> = {};

  const type = input.registrantType;
  if (type !== 'contractor' && type !== 'worker') errors.registrantType = 'יש לבחור אם נרשמים כקבלן או כעובד';

  const fullName = (input.fullName ?? '').trim().replace(/\s+/g, ' ');
  if (fullName.length < 2) errors.fullName = 'יש להזין שם מלא';
  else if (fullName.length > LIMITS.maxNameLength) errors.fullName = 'השם ארוך מדי';

  const phone = parseIsraeliPhone(input.phone ?? '');
  if (!phone.ok) errors.phone = PHONE_ERROR_MESSAGES[phone.reason];

  let workerCount = 1;
  if (type === 'contractor') {
    const n = input.workerCount;
    if (typeof n !== 'number' || !Number.isInteger(n)) errors.workerCount = 'יש להזין כמות עובדים במספר שלם';
    else if (n < LIMITS.minWorkers) errors.workerCount = `הכמות המינימלית היא ${LIMITS.minWorkers}`;
    else if (n > maxWorkers) errors.workerCount = `הכמות המקסימלית היא ${maxWorkers}`;
    else workerCount = n;
  }

  if (Object.keys(errors).length > 0 || !phone.ok) return { ok: false, errors };
  return { ok: true, value: { registrantType: type, fullName, phoneE164: phone.e164, workerCount: effectiveWorkerCount(type, workerCount) } };
}
